import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaAt,
  FaBell,
  FaBriefcase,
  FaCheckCircle,
  FaCheckDouble,
  FaCompass,
  FaHeart,
  FaNewspaper,
  FaShieldAlt,
  FaTimesCircle,
  FaUser,
  FaUserCog,
  FaUserPlus,
} from 'react-icons/fa';
import VihsMark from '../VihsMark';
import LanguageSwitcher from '../LanguageSwitcher';
import { socialService } from '../../services/apiService';
import { formatRelative } from '../../i18n/formatRelative';
import './SocialNav.css';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name || '?').slice(0, 2).toUpperCase();
}

function notificationCopy(item, t) {
  if (item.title && item.text) {
    return { title: item.title, text: item.text };
  }
  if (item.type === 'report_reviewed') {
    return {
      title: t('notifications.moderationTitle'),
      text: t('notifications.reportReviewed'),
    };
  }
  if (item.type === 'report_dismissed') {
    return {
      title: t('notifications.moderationTitle'),
      text: t('notifications.reportDismissed'),
    };
  }

  const name = item.actor?.profile?.displayName || item.actor?.username || t('common.someone');
  if (item.type === 'follow') return { title: name, text: t('notifications.startedFollowing') };
  if (item.type === 'like') return { title: name, text: t('notifications.likedPost') };
  if (item.type === 'mention') {
    if (item.meta?.note === 'hub') {
      return { title: name, text: t('notifications.mentionedHub') };
    }
    return { title: name, text: t('notifications.mentionedPost') };
  }
  if (item.type === 'post') return { title: name, text: t('notifications.newPost') };
  return { title: name, text: t('notifications.sentUpdate') };
}

function NotificationIcon({ type }) {
  if (type === 'like') return <FaHeart />;
  if (type === 'follow') return <FaUserPlus />;
  if (type === 'mention') return <FaAt />;
  if (type === 'report_reviewed') return <FaCheckCircle />;
  if (type === 'report_dismissed') return <FaTimesCircle />;
  if (type === 'post') return <FaNewspaper />;
  if (type === 'project_received' || type === 'project_status') return <FaBriefcase />;
  return <FaShieldAlt />;
}

export default function SocialNav({ username }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const links = [
    { to: '/feed', label: t('nav.feed'), icon: FaNewspaper },
    { to: '/explorar', label: t('nav.explore'), icon: FaCompass },
    ...(username ? [{ to: `/perfil/${username}`, label: t('nav.myProfile'), icon: FaUser }] : []),
  ];

  const refreshUnread = useCallback(async () => {
    try {
      const res = await socialService.getUnreadNotificationCount();
      setUnreadCount(Number(res.data?.unreadCount) || 0);
    } catch {
      /* silencioso no poll */
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await socialService.getNotifications({ limit: 20 });
      setItems(res.data?.notifications || []);
      setUnreadCount(Number(res.data?.unreadCount) || 0);
    } catch {
      /* mantém lista atual */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    const timer = window.setInterval(refreshUnread, 30000);
    return () => window.clearInterval(timer);
  }, [refreshUnread]);

  useEffect(() => {
    if (!open) return undefined;

    loadNotifications();

    const onPointerDown = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, loadNotifications]);

  const handleOpenToggle = () => {
    setOpen((current) => !current);
  };

  const handleMarkAll = async () => {
    try {
      await socialService.markAllNotificationsRead();
      setItems((current) => current.map((item) => (
        item.readAt ? item : { ...item, readAt: new Date().toISOString() }
      )));
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  };

  const handleOpenItem = async (item) => {
    if (!item.readAt) {
      setItems((current) => current.map((row) => (
        row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row
      )));
      setUnreadCount((count) => Math.max(0, count - 1));
      socialService.markNotificationRead(item.id).catch(() => null);
    }

    setOpen(false);

    if (item.type === 'report_reviewed' || item.type === 'report_dismissed') {
      return;
    }

    if (item.type === 'project_received' || item.type === 'project_status') {
      navigate('/conta#account-pedidos');
      return;
    }

    if (item.type === 'follow' || item.type === 'post') {
      navigate(`/perfil/${item.actor?.username || ''}`);
      return;
    }

    if (item.type === 'like' || item.type === 'mention') {
      if (item.type === 'mention' && item.meta?.note === 'hub' && item.actor?.username) {
        navigate(`/perfil/${item.actor.username}`);
        return;
      }
      if (item.post?.id) {
        navigate(`/feed?post=${encodeURIComponent(item.post.id)}`);
      } else if (item.actor?.username) {
        navigate(`/perfil/${item.actor.username}`);
      } else {
        navigate('/feed');
      }
    }
  };

  const renderLinks = (linkClassName) => (
    links.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        className={({ isActive }) => `${linkClassName}${isActive ? ' is-active' : ''}`}
      >
        <Icon aria-hidden="true" />
        <span>{label}</span>
      </NavLink>
    ))
  );

  return (
    <>
      <header className="social-nav">
        <Link to="/" className="social-nav-brand" aria-label={t('nav.homeAria')}>
          <span className="social-nav-logo"><VihsMark /></span>
          <span className="social-nav-wordmark">Vih&apos;s</span>
          <span className="social-nav-product">Dev Hub</span>
        </Link>

        <nav className="social-nav-links" aria-label={t('nav.socialAria')}>
          {renderLinks('social-nav-link')}
        </nav>

        <div className="social-nav-actions">
          <LanguageSwitcher className="social-nav-lang-desktop" />
          <LanguageSwitcher className="social-nav-lang-mobile" compact />
          <div className="social-notify" ref={panelRef}>
            <button
              type="button"
              className={`social-notify-trigger${open ? ' is-open' : ''}`}
              aria-label={t('nav.notifications')}
              aria-expanded={open}
              onClick={handleOpenToggle}
            >
              <FaBell aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="social-notify-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {open && (
              <div className="social-notify-panel" role="dialog" aria-label={t('nav.notifications')}>
                <div className="social-notify-head">
                  <div>
                    <strong>{t('nav.notifications')}</strong>
                    <span>
                      {unreadCount > 0
                        ? t('nav.notificationsNew', { count: unreadCount })
                        : t('nav.notificationsAllCaughtUp')}
                    </span>
                  </div>
                  {unreadCount > 0 && (
                    <button type="button" className="social-notify-mark-all" onClick={handleMarkAll}>
                      <FaCheckDouble aria-hidden="true" />
                      {t('nav.markAllRead')}
                    </button>
                  )}
                </div>

                <div className="social-notify-list">
                  {loading && items.length === 0 ? (
                    <p className="social-notify-empty">{t('common.loading')}</p>
                  ) : items.length === 0 ? (
                    <p className="social-notify-empty">{t('nav.noNotifications')}</p>
                  ) : (
                    items.map((item) => {
                      const copy = notificationCopy(item, t);
                      const isModeration = item.type === 'report_reviewed' || item.type === 'report_dismissed';
                      const isProject = item.type === 'project_received' || item.type === 'project_status';
                      const actorName = item.actor?.profile?.displayName || item.actor?.username;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`social-notify-item${!item.readAt ? ' is-unread' : ''}${isModeration || isProject ? ' is-moderation' : ''}`}
                          onClick={() => handleOpenItem(item)}
                        >
                          <span className={`social-notify-avatar${isModeration || isProject ? ' is-moderation' : ''}`} aria-hidden="true">
                            {isModeration
                              ? <FaShieldAlt />
                              : isProject
                                ? <FaBriefcase />
                                : item.actor?.avatarUrl
                                  ? <img src={item.actor.avatarUrl} alt="" />
                                  : getInitials(actorName)}
                            <span className={`social-notify-type is-${item.type}`}>
                              <NotificationIcon type={item.type} />
                            </span>
                          </span>
                          <span className="social-notify-copy">
                            <strong>{copy.title}</strong>
                            <em>{copy.text}</em>
                            {!isModeration && item.type !== 'follow' && item.post?.content && (
                              <small className="social-notify-snippet">
                                “{item.post.content.slice(0, 72)}{item.post.content.length > 72 ? '…' : ''}”
                              </small>
                            )}
                            <small>{formatRelative(item.createdAt, t)}</small>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <Link to="/conta" className="social-nav-account" aria-label={t('nav.myAccount')}>
            <FaUserCog aria-hidden="true" />
            <span>{t('nav.myAccount')}</span>
          </Link>
          <Link to="/" className="social-nav-back" aria-label={t('nav.backHomeAria')}>
            <FaArrowLeft aria-hidden="true" />
          </Link>
        </div>
      </header>

      <nav className="social-nav-tabs" aria-label={t('nav.socialAria')}>
        {renderLinks('social-nav-tab')}
      </nav>
      <div className="social-nav-tabs-spacer" aria-hidden="true" />
    </>
  );
}
